import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ColumnDef } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";
import { Eye, MoreHorizontal, Pencil, Plus, Trash2, X } from "lucide-react";
import { useSearchParams } from "react-router-dom";

import { ConfirmDelete } from "@/components/admin/ConfirmDelete";
import { AuditGhostBanner, AuditGhostRecord } from "@/components/admin/AuditGhostRecord";
import { EntityContacts } from "@/components/admin/EntityContacts";
import {
  CheckboxField,
  Field,
  NumberField,
  SelectField,
  TextField,
} from "@/components/admin/fields";
import { DataTable } from "@/components/data/DataTable";
import { toast } from "@/components/feedback/ToastHost";
import { EmptyState } from "@/components/feedback/EmptyState";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { errorMessage } from "@/lib/errors";
import { isDeletedRecord, useAuditHighlight } from "@/lib/auditHighlight";
import {
  createCustomer,
  deleteCustomer,
  getCustomer,
  listCustomers,
  updateCustomer,
} from "@/services/api/customers";
import { listHauliers } from "@/services/api/hauliers";
import { listCse } from "@/services/api/cse";
import { listPalletTypes } from "@/services/api/loadPlans";
import { listRateCards } from "@/services/api/rateCards";
import type { Customer } from "@/services/api/types";

interface CseAssignmentForm {
  cse_id: string;
  is_permanent: boolean;
  start_date: string;
  end_date: string;
}

const emptyForm = {
  customer_no: "",
  name: "",
  address: "",
  postcode: "",
  default_haulier_id: "",
  rate_card_id: "",
  invoice_id: "",
  double_deckers_allowed: false,
  default_pallet_type_id: "",
  max_pallet_count_per_load: "",
  loading_time_minutes: "",
  travel_time_minutes: "",
  customer_to_collect: false,
  cse_assignments: [] as CseAssignmentForm[],
  linked_customer_ids: [] as number[],
};

export function CustomersPage() {
  const { t } = useTranslation();
  const [offset, setOffset] = useState(0);
  const limit = 25;
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  // Audit deep-link context (?r=<id>&a=<action>&f=<fields>&ar=<auditId>).
  const audit = useAuditHighlight();
  const ghostRecord = isDeletedRecord(audit);

  const list = useQuery({
    queryKey: ["customers", offset],
    queryFn: () => listCustomers({ limit, offset }),
  });
  const hauliers = useQuery({ queryKey: ["hauliers", "all"], queryFn: () => listHauliers({ limit: 500 }) });
  const cses = useQuery({ queryKey: ["cse", "all"], queryFn: () => listCse({ limit: 500 }) });
  const rateCards = useQuery({ queryKey: ["rate-cards", "all"], queryFn: () => listRateCards({ limit: 500 }) });
  const palletTypes = useQuery({ queryKey: ["pallet-types", "all"], queryFn: () => listPalletTypes(true) });
  const allCustomers = useQuery({ queryKey: ["customers", "all"], queryFn: () => listCustomers({ limit: 500 }) });

  const [dialog, setDialog] = useState<
    | { mode: "create" }
    | { mode: "view"; record: Customer; ghost?: boolean }
    | { mode: "edit"; record: Customer }
    | null
  >(null);
  const [deleting, setDeleting] = useState<Customer | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [highlight, setHighlight] = useState<string | null>(searchParams.get("r"));
  // True when the deleted customer cannot be fetched (hard-deleted) — the
  // audit-replay ghost card above the table is shown instead of the real one.
  const [ghostCard, setGhostCard] = useState(false);
  // Result of the Google Maps address validation (shown on the edit form).
  const [geocodeNotice, setGeocodeNotice] = useState<{ ok: boolean; text: string } | null>(
    null,
  );

  const noticeFor = (record: Customer | null): { ok: boolean; text: string } | null => {
    if (!record) return null;
    if (record.latitude != null && record.longitude != null) {
      return {
        ok: true,
        text: `Address validated (${record.latitude.toFixed(4)}, ${record.longitude.toFixed(4)}).`,
      };
    }
    if (record.geocode_status === "failed") {
      return { ok: false, text: "Address could not be validated by Google Maps." };
    }
    return null;
  };

  const refresh = () => void queryClient.invalidateQueries({ queryKey: ["customers"] });

  const save = useMutation({
    mutationFn: async () => {
      const payload = {
        customer_no: form.customer_no,
        name: form.name,
        address: form.address || null,
        postcode: form.postcode || null,
        default_haulier_id: form.default_haulier_id ? Number(form.default_haulier_id) : null,
        rate_card_id: form.rate_card_id ? Number(form.rate_card_id) : null,
        invoice_id: form.invoice_id || null,
        double_deckers_allowed: form.double_deckers_allowed,
        default_pallet_type_id: form.default_pallet_type_id
          ? Number(form.default_pallet_type_id)
          : null,
        max_pallet_count_per_load: form.max_pallet_count_per_load
          ? Number(form.max_pallet_count_per_load)
          : null,
        loading_time_minutes: form.loading_time_minutes
          ? Number(form.loading_time_minutes)
          : null,
        travel_time_minutes: form.travel_time_minutes ? Number(form.travel_time_minutes) : null,
        customer_to_collect: form.customer_to_collect,
        cse_assignments: form.cse_assignments
          .filter((assignment) => assignment.cse_id !== "")
          .map((assignment) => ({
            cse_id: Number(assignment.cse_id),
            is_permanent: assignment.is_permanent,
            start_date: assignment.is_permanent ? null : (assignment.start_date || null),
            end_date: assignment.is_permanent ? null : (assignment.end_date || null),
          })),
        linked_customer_ids: form.linked_customer_ids,
      };
      if (dialog?.mode === "edit") {
        return updateCustomer(dialog.record.id, payload);
      }
      return createCustomer(payload);
    },
    onSuccess: (created) => {
      toast.success(t("admin.saved"));
      refresh();
      const saved = created as Customer;
      // Surface the Google Maps address-validation result.
      if (saved.geocode_status === "ok" && saved.latitude != null) {
        setGeocodeNotice({
          ok: true,
          text: "Address validated — location saved for the route planner.",
        });
      } else if (saved.geocode_status === "failed") {
        setGeocodeNotice({
          ok: false,
          text: "Address could not be validated — enable the Google Geocoding API for the Maps API key, then re-save.",
        });
      } else {
        setGeocodeNotice(null);
      }
      // After a create, stay on the same form in edit mode so contacts (and
      // anything else) can be added — create and edit feel like one form.
      if (
        dialog?.mode === "create" &&
        created &&
        typeof (created as { id?: unknown }).id === "number"
      ) {
        openEdit(created as Customer);
      } else {
        setDialog(null);
      }
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const remove = useMutation({
    mutationFn: (id: number) => deleteCustomer(id),
    onSuccess: () => {
      toast.success(t("admin.deleted"));
      setDeleting(null);
      refresh();
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const openEdit = (record: Customer) => {
    setForm({
      customer_no: record.customer_no,
      name: record.name,
      address: record.address ?? "",
      postcode: record.postcode ?? "",
      default_haulier_id: record.default_haulier_id?.toString() ?? "",
      rate_card_id: record.rate_card_id?.toString() ?? "",
      invoice_id: record.invoice_id ?? "",
      double_deckers_allowed: record.double_deckers_allowed,
      default_pallet_type_id: record.default_pallet_type_id?.toString() ?? "",
      max_pallet_count_per_load: record.max_pallet_count_per_load?.toString() ?? "",
      loading_time_minutes: record.loading_time_minutes?.toString() ?? "",
      travel_time_minutes: record.travel_time_minutes?.toString() ?? "",
      customer_to_collect: record.customer_to_collect,
      cse_assignments: (record.cse_assignments ?? []).map((assignment) => ({
        cse_id: assignment.cse_id.toString(),
        is_permanent: assignment.is_permanent,
        start_date: assignment.start_date ?? "",
        end_date: assignment.end_date ?? "",
      })),
      linked_customer_ids: record.linked_customer_ids ?? [],
    });
    setGeocodeNotice(noticeFor(record));
    setDialog({ mode: "edit", record });
  };

  const openCreate = () => {
    setForm(emptyForm);
    setGeocodeNotice(null);
    setDialog({ mode: "create" });
  };

  // Audit deep-link support: /admin/customers?r=123 opens the record's view
  // dialog. A delete deep-link fetches the retained (soft-deleted) customer —
  // the get endpoint returns it — and displays the real record in a
  // ghost-styled view dialog; only when it cannot be fetched does the
  // audit-replay ghost card above the table take over.
  useEffect(() => {
    if (!audit.recordId || ghostRecord || list.isLoading) return;
    const record = (list.data?.items ?? []).find((c) => String(c.id) === audit.recordId);
    if (record) setDialog({ mode: "view", record });
  }, [audit.recordId, ghostRecord, list.data, list.isLoading]);

  useEffect(() => {
    if (!ghostRecord || !audit.recordId) {
      setGhostCard(false);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const record = await getCustomer(Number(audit.recordId));
        if (!cancelled && record) setDialog({ mode: "view", record, ghost: true });
      } catch {
        if (!cancelled) setGhostCard(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [ghostRecord, audit.recordId]);

  const columns: ColumnDef<Customer>[] = [
    { accessorKey: "customer_no", header: t("admin.customerNo") },
    { accessorKey: "name", header: t("admin.name") },
    {
      header: t("admin.defaultHaulier"),
      cell: ({ row }) => {
        const haulier = hauliers.data?.items.find((h) => h.id === row.original.default_haulier_id);
        return haulier?.name ?? "—";
      },
    },
    {
      header: t("admin.cse"),
      cell: ({ row }) => {
        const cse = cses.data?.items.find((c) => c.id === row.original.cse_id);
        return cse ? `${cse.name} ${cse.surname}` : "—";
      },
    },
    {
      accessorKey: "double_deckers_allowed",
      header: t("admin.doubleDeckers"),
      cell: ({ getValue }) => (getValue() ? t("common.yes") : t("common.no")),
    },
    {
      id: "actions",
      header: t("common.actions"),
      cell: ({ row }) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => setDialog({ mode: "view", record: row.original })}>
              <Eye className="h-4 w-4" /> {t("common.view")}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => openEdit(row.original)}>
              <Pencil className="h-4 w-4" /> {t("common.edit")}
            </DropdownMenuItem>
            <DropdownMenuItem
              className="text-destructive focus:text-destructive"
              onClick={() => setDeleting(row.original)}
            >
              <Trash2 className="h-4 w-4" /> {t("common.delete")}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  const haulierOptions = (hauliers.data?.items ?? []).map((h) => ({
    value: h.id.toString(),
    label: `${h.haulier_code} — ${h.name}`,
  }));
  const rateCardOptions = (rateCards.data?.items ?? []).map((card) => ({
    value: card.id.toString(),
    label: `#${card.id} — ${card.currency} ${card.rate_standard}`,
  }));
  const cseOptions = (cses.data?.items ?? []).map((c) => ({
    value: c.id.toString(),
    label: `${c.cse_code} — ${c.name} ${c.surname}`,
  }));
  const customerLinkOptions = (allCustomers.data?.items ?? [])
    .filter((customer) => dialog?.mode !== "edit" || customer.id !== dialog.record.id)
    .map((customer) => ({ id: customer.id, label: `${customer.customer_no} — ${customer.name}` }));
  const toggleLink = (id: number) =>
    setForm((f) => ({
      ...f,
      linked_customer_ids: f.linked_customer_ids.includes(id)
        ? f.linked_customer_ids.filter((x) => x !== id)
        : [...f.linked_customer_ids, id],
    }));

  const updateAssignment = (index: number, patch: Partial<CseAssignmentForm>) =>
    setForm((f) => ({
      ...f,
      cse_assignments: f.cse_assignments.map((assignment, i) =>
        i === index ? { ...assignment, ...patch } : assignment,
      ),
    }));
  const removeAssignment = (index: number) =>
    setForm((f) => ({
      ...f,
      cse_assignments: f.cse_assignments.filter((_, i) => i !== index),
    }));
  const addAssignment = () =>
    setForm((f) => ({
      ...f,
      cse_assignments: [
        ...f.cse_assignments,
        { cse_id: "", is_permanent: true, start_date: "", end_date: "" },
      ],
    }));

  const formBody = (
    <div className="grid grid-cols-2 gap-4">
      <TextField
        label={t("admin.customerNo")}
        value={form.customer_no}
        onChange={(v) => setForm((f) => ({ ...f, customer_no: v }))}
        required
      />
      <TextField
        label={t("admin.invoiceId")}
        value={form.invoice_id}
        onChange={(v) => setForm((f) => ({ ...f, invoice_id: v }))}
      />
      <TextField
        label={t("admin.name")}
        value={form.name}
        onChange={(v) => setForm((f) => ({ ...f, name: v }))}
        required
      />
      <SelectField
        label={t("admin.defaultHaulier")}
        value={form.default_haulier_id}
        onChange={(v) => setForm((f) => ({ ...f, default_haulier_id: v }))}
        options={[{ value: "", label: "—" }, ...haulierOptions]}
        placeholder={t("admin.none")}
      />
      <SelectField
        label={t("admin.rateCard")}
        value={form.rate_card_id}
        onChange={(v) => setForm((f) => ({ ...f, rate_card_id: v }))}
        options={[{ value: "", label: "—" }, ...rateCardOptions]}
        placeholder={t("admin.none")}
      />
      <TextField
        label={t("admin.address")}
        value={form.address}
        onChange={(v) => setForm((f) => ({ ...f, address: v }))}
      />
      <NumberField
        label={t("admin.maxPalletCount")}
        value={form.max_pallet_count_per_load}
        onChange={(v) => setForm((f) => ({ ...f, max_pallet_count_per_load: v }))}
      />
      <TextField
        label={t("admin.postcode")}
        value={form.postcode}
        onChange={(v) => setForm((f) => ({ ...f, postcode: v }))}
      />
      {geocodeNotice && (
        <div
          className={`col-span-2 text-sm ${
            geocodeNotice.ok ? "text-emerald-600" : "text-amber-600"
          }`}
        >
          {geocodeNotice.ok ? "✓ " : "⚠ "}
          {geocodeNotice.text}
        </div>
      )}
      <NumberField
        label={t("admin.loadingTimeMinutes")}
        value={form.loading_time_minutes}
        onChange={(v) => setForm((f) => ({ ...f, loading_time_minutes: v }))}
      />
      <NumberField
        label={t("admin.travelTimeMinutes")}
        value={form.travel_time_minutes}
        onChange={(v) => setForm((f) => ({ ...f, travel_time_minutes: v }))}
      />
      <div className="col-span-2 rounded-md border p-3">
        <div className="mb-3 flex items-center justify-between">
          <Label className="text-sm font-semibold">Customer Service Executives (CSE)</Label>
          <Button type="button" variant="outline" size="sm" onClick={addAssignment}>
            <Plus className="h-4 w-4" />
            Add CSE
          </Button>
        </div>
        <div className="space-y-2">
          {form.cse_assignments.map((assignment, index) => (
            <div
              key={index}
              className="flex flex-wrap items-end gap-3 rounded border bg-muted/20 p-3"
            >
            <div className="w-56">
              <Label className="text-xs">CSE</Label>
              <Select
                value={assignment.cse_id}
                onValueChange={(v) => updateAssignment(index, { cse_id: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="— none —" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">— none —</SelectItem>
                  {cseOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <label className="flex items-center gap-2 pb-2 text-xs text-muted-foreground">
              <Checkbox
                checked={assignment.is_permanent}
                onCheckedChange={(v) => updateAssignment(index, { is_permanent: v === true })}
              />
              Permanent
            </label>
            {!assignment.is_permanent ? (
              <>
                <div>
                  <Label className="text-xs">Start date</Label>
                  <Input
                    type="date"
                    value={assignment.start_date}
                    onChange={(e) => updateAssignment(index, { start_date: e.target.value })}
                  />
                </div>
                <div>
                  <Label className="text-xs">End date</Label>
                  <Input
                    type="date"
                    value={assignment.end_date}
                    onChange={(e) => updateAssignment(index, { end_date: e.target.value })}
                  />
                </div>
              </>
            ) : null}
            <Button
              type="button"
              variant="ghost"
              size="icon"
              title="Remove CSE"
              className="pb-2"
              onClick={() => removeAssignment(index)}
            >
              <X className="h-4 w-4" />
            </Button>
            </div>
          ))}
        </div>
      </div>
      <div className="col-span-2 rounded-md border p-3">
        <Label className="text-sm font-semibold">Linked customers</Label>
        <p className="mb-2 text-xs text-muted-foreground">
          Linked customers' loads are stacked together on the route planner map.
        </p>
        <div className="max-h-40 space-y-1 overflow-y-auto">
          {customerLinkOptions.map((option) => (
            <label key={option.id} className="flex items-center gap-2 text-sm">
              <Checkbox
                checked={form.linked_customer_ids.includes(option.id)}
                onCheckedChange={() => toggleLink(option.id)}
              />
              {option.label}
            </label>
          ))}
        </div>
      </div>
      <div className="col-span-2 space-y-2">
        <CheckboxField
          label={t("admin.doubleDeckers")}
          checked={form.double_deckers_allowed}
          onChange={(v) => setForm((f) => ({ ...f, double_deckers_allowed: v }))}
        />
        <SelectField
          label={t("admin.defaultPalletType")}
          value={form.default_pallet_type_id}
          onChange={(v) => setForm((f) => ({ ...f, default_pallet_type_id: v }))}
          options={[
            { value: "", label: t("common.none") },
            ...(palletTypes.data ?? []).map((type) => ({
              value: type.id.toString(),
              label: type.name,
            })),
          ]}
        />
        <CheckboxField
          label={t("admin.customerToCollect")}
          checked={form.customer_to_collect}
          onChange={(v) => setForm((f) => ({ ...f, customer_to_collect: v }))}
        />
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="text-2xl font-semibold">{t("nav.customers")}</h1>
        </div>
        <Button onClick={openCreate}>{t("admin.create")}</Button>
      </div>

      {!list.isLoading && (list.data?.total ?? 0) === 0 && !ghostRecord ? (
        <EmptyState title={t("common.emptyState")} />
      ) : (
        <>
          {ghostRecord && ghostCard ? (
            <AuditGhostRecord
              entityType={audit.entityType ?? "customer"}
              entityId={audit.recordId}
            />
          ) : null}
          <DataTable
            columns={columns}
            data={list.data?.items ?? []}
            total={list.data?.total ?? 0}
            limit={limit}
            offset={offset}
            onPageChange={setOffset}
            isLoading={list.isLoading}
            highlightId={highlight}
            onRowDoubleClick={(record) => setDialog({ mode: "view", record })}
          />
        </>
      )}

      <Dialog
        open={dialog !== null}
        onOpenChange={(o) => {
          if (!o) {
            setDialog(null);
            // Leaving the deep-linked record also drops its row highlight.
            setHighlight(null);
          }
        }}
      >
        <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {dialog?.mode === "create"
                ? t("admin.create")
                : dialog?.mode === "edit"
                  ? t("admin.edit")
                  : t("admin.view")}
            </DialogTitle>
            {dialog?.mode !== "create" ? (
              <DialogDescription>{dialog?.record.customer_no}</DialogDescription>
            ) : null}
          </DialogHeader>
          {dialog?.mode === "view" ? (
            <div
              className={`space-y-4${
                // Ghost styling: the retained (soft-deleted) customer.
                dialog.ghost ? " audit-ghost-record p-4" : ""
              }`}
            >
              {dialog.ghost ? <AuditGhostBanner /> : null}
              <div className="grid grid-cols-2 gap-4">
              <Field
                label={t("admin.customerNo")}
                value={dialog.record.customer_no}
                field="customer_no"
              />
              <Field label={t("admin.invoiceId")} value={dialog.record.invoice_id} field="invoice_id" />
              <Field label={t("admin.name")} value={dialog.record.name} field="name" />
              <Field
                label={t("admin.defaultHaulier")}
                value={
                  hauliers.data?.items.find((h) => h.id === dialog.record.default_haulier_id)?.name
                }
                field="default_haulier_id"
              />
              <Field
                label={t("admin.rateCard")}
                value={dialog.record.rate_card_id ? `#${dialog.record.rate_card_id}` : undefined}
                field="rate_card_id"
              />
              <Field label={t("admin.address")} value={dialog.record.address} field="address" />
              <Field
                label={t("admin.maxPalletCount")}
                value={dialog.record.max_pallet_count_per_load}
                field="max_pallet_count_per_load"
              />
              <Field label={t("admin.postcode")} value={dialog.record.postcode} field="postcode" />
              <Field
                label={t("admin.loadingTimeMinutes")}
                value={dialog.record.loading_time_minutes}
                field="loading_time_minutes"
              />
              <Field
                label={t("admin.travelTimeMinutes")}
                value={dialog.record.travel_time_minutes}
                field="travel_time_minutes"
              />
              <Field
                label={t("admin.doubleDeckers")}
                value={dialog.record.double_deckers_allowed ? t("common.yes") : t("common.no")}
                field="double_deckers_allowed"
              />
              <Field
                label={t("admin.defaultPalletType")}
                value={
                  (palletTypes.data ?? []).find(
                    (type) => type.id === dialog.record.default_pallet_type_id,
                  )?.name ?? "—"
                }
                field="default_pallet_type_id"
              />
              <Field
                label={t("admin.customerToCollect")}
                value={dialog.record.customer_to_collect ? t("common.yes") : t("common.no")}
                field="customer_to_collect"
              />
              <div className="col-span-2">
                <Field
                  label="Customer Service Executives (CSE)"
                  value={
                    (dialog.record.cse_assignments ?? []).length > 0 ? (
                      <ul className="list-inside list-disc space-y-0.5">
                        {dialog.record.cse_assignments.map((assignment) => {
                          const cse = cses.data?.items.find((c) => c.id === assignment.cse_id);
                          const label = cse ? `${cse.cse_code} — ${cse.name} ${cse.surname}` : "?";
                          return (
                            <li key={assignment.id}>
                              {label}
                              {assignment.is_permanent
                                ? " (Permanent)"
                                : ` (Temporary: ${assignment.start_date ?? "?"} → ${assignment.end_date ?? "?"})`}
                            </li>
                          );
                        })}
                      </ul>
                    ) : undefined
                  }
                />
              </div>
              <div className="col-span-2">
                <EntityContacts ownerType="customer" ownerId={dialog.record.id} readOnly />
              </div>
              </div>
            </div>
          ) : dialog ? (
            <>
              <div className="max-h-[55vh] space-y-4 overflow-y-auto">
                {formBody}
                <div className="pt-4">
                  <EntityContacts
                    ownerType="customer"
                    ownerId={
                      dialog.mode === "edit" && dialog.record ? dialog.record.id : null
                    }
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setDialog(null)}>
                  {t("common.cancel")}
                </Button>
                <Button onClick={() => save.mutate()} disabled={save.isPending || !form.name}>
                  {t("common.save")}
                </Button>
              </div>
            </>
          ) : null}
        </DialogContent>
      </Dialog>

      <ConfirmDelete
        open={deleting !== null}
        onOpenChange={(o) => !o && setDeleting(null)}
        title={t("admin.deleteRecord", { name: deleting?.customer_no ?? "" })}
        description={t("admin.deleteRecordDescription")}
        onConfirm={async () => {
          if (deleting) await remove.mutateAsync(deleting.id);
        }}
      />
    </div>
  );
}
